/**
 * A function to create a log object for the 'clickTheWasItHelpfulButtons' event (evid: 501)
 *
 * Event description: fired when a user votes helpful/unhelpful
 *
 * @param {clickTheWasItHelpfulButtonsParams} params - The parameters for this event
 * @property {string} item_url - item_url
 * @property {GUID} item_id - item_id
 * @property {boolean} thumbs_up - Indicate if the user vote helpful or unhelpful
 * @property {string} feedback_details - feedback_details
 * @property {string} item_name - item_name
 * @property {string} source_name - indicates what page the user is at: home, article, category, search_results
 * @property {string} category - category
 * @property {string} kb_lang - kb page language
 * @property {string} lang - wix language
 * @property {string} item_type - item_type
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @property {string} clicked_text - the answer the user selected
 * @return {LogObject<clickTheWasItHelpfulButtonsParams>} LogObject<clickTheWasItHelpfulButtonsParams>
 */
export function clickTheWasItHelpfulButtons(params) {
    return { evid: 501, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'voteUnVoteForWishList' event (evid: 502)
 *
 * Event description: fired when a user votes / unvote for a feature request
 *
 * @param {voteUnVoteForWishListParams} params - The parameters for this event
 * @property {string} item_url - item_url
 * @property {GUID} item_id - include the article_id
 * @property {boolean} thumbs_up - Indicate if the user vote or unvote
 * @property {string} tab - tab
 * @property {string} filter_by - filter_by
 * @property {string} item_name - item_name
 * @property {string} vote_type - vote_type
 * @property {string} source_name - the kb page the vote occurred on. Can be article or roadmap.
 * @property {string} sort_by - sort_by
 * @property {string} category - category
 * @property {string} kb_lang - kb page language
 * @property {string} lang - wix language
 * @property {string} source_component_name - source_component_name
 * @property {string} item_type - item_type
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<voteUnVoteForWishListParams>} LogObject<voteUnVoteForWishListParams>
 */
export function voteUnVoteForWishList(params) {
    return { evid: 502, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'clickTheIHaveThisProblemBugButton' event (evid: 503)
 *
 * Event description: fire when a user votes/ unvote for a known issue
 *
 * @param {clickTheIHaveThisProblemBugButtonParams} params - The parameters for this event
 * @property {string} item_url - item_url
 * @property {string} item_id - item_id
 * @property {boolean} thumbs_up - Indicate if the user vote or unvote
 * @property {string} item_name - item_name
 * @property {string} source_name - indicates what page the user is at: home, article, category, search_results
 * @property {string} category - category
 * @property {string} kb_lang - kb page language
 * @property {string} lang - wix language
 * @property {string} item_type - item_type
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<clickTheIHaveThisProblemBugButtonParams>} LogObject<clickTheIHaveThisProblemBugButtonParams>
 */
export function clickTheIHaveThisProblemBugButton(params) {
    return { evid: 503, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'clickTheFollowUnFollowButton' event (evid: 504)
 *
 * Event description: fired when users (wix users) click the top bar chackbox to follow/unfollow an article in the help center
 *
 * @param {clickTheFollowUnFollowButtonParams} params - The parameters for this event
 * @property {string} item_url - item_url
 * @property {GUID} item_id - the article's id
 * @property {boolean} thumbs_up - Indicate if the user follow or unfollow
 * @property {string} source_name - indicates what page the user is at: home, article, category, search_results
 * @property {string} kb_lang - kb_lang
 * @property {string} lang - lng
 * @property {string} item_type - item_type
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<clickTheFollowUnFollowButtonParams>} LogObject<clickTheFollowUnFollowButtonParams>
 */
export function clickTheFollowUnFollowButton(params) {
    return { evid: 504, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'askingAQuestion' event (evid: 520)
 *
 * Event description: Fire when the search results returned after user searched in the Help Center
 *
 * @param {askingAQuestionParams} params - The parameters for this event
 * @property {string} question - The search term the user searched for
 * @property {string} source_url - source_url
 * @property {string} item_id - item_id
 * @property {string} search_method - search_method
 * @property {string} search_session_Id - search_session_id
 * @property {string} presented_content - presented_content
 * @property {string} first_answer - first_answer
 * @property {string} source_name - indicates what page the user conducted the search is at: home, article, category, search_results
 * @property {string} search_version - name of search model used
 * @property {string} kb_lang - kb page language
 * @property {boolean} has_related - has_related
 * @property {string} lang - wix language
 * @property {DateTime} action_date - action_date
 * @property {string} item_type - item_type
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @property {string} summarized_answer_text - The full generated answer that was shown
 * @property {string} minimum_retrieval_distance - The lower distance from all the articles that were returned by the KA retrieval mechanisms.
 * @property {string} summarized_answer_type - The type of answer that was generated ('support request','prohibited','direct')
 * @property {boolean} is_support_request - Is the user query   was marked as support request by the support classifier
 * @property {string} context_msid - The user's site that was taken into consideration when returning the search results
 * @property {string} context_editor_type - The user's site editor type that was taken into consideration when returning the search results
 * @property {string} input_type - The type of input the user used to search (e.g. 'suggested question')
 * @property {string} summarized_answer_based_on - The list of articles the generated answer was based on
 * @property {number} summarized_answer_words_count - Number of words the generated answer was include
 * @return {LogObject<askingAQuestionParams>} LogObject<askingAQuestionParams>
 */
export function askingAQuestion(params) {
    return { evid: 520, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'clickOnAnswer' event (evid: 521)
 *
 * Event description: Fire when the user selects one of the search results or interact with an answer in the Help Center
 *
 * @param {clickOnAnswerParams} params - The parameters for this event
 * @property {string} item_url - item_url
 * @property {string} question - The search term the user searched for
 * @property {string} search_result_selection - search_result_selection
 * @property {string} source_url - source_url
 * @property {string} item_id - item_id
 * @property {boolean} is_related - is_related
 * @property {string} search_method - search_method
 * @property {string} search_session_Id - search_session_id
 * @property {string} clicked_item_order - clicked_item_order
 * @property {string} clicked_item_type - clicked_item_type
 * @property {string} clicked_item_id - clicked_item_id
 * @property {string} source_name - indicates what page the user is at: home, article, category, search_results
 * @property {string} search_version - name of search model used
 * @property {string} order - order
 * @property {string} answer - answer
 * @property {string} kb_lang - kb page language
 * @property {boolean} has_related - has_related
 * @property {string} lang - wix language
 * @property {DateTime} action_date - action_date
 * @property {string} item_type - item_type
 * @property {number} page_number - The results page number on which the results the user chose was on
 * @property {string} clicked_url - clicked_url
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @property {string} summarized_answer_text - The full generated answer that was shown
 * @property {string} summarized_answer_type - The type of answer that was generated ('support request','prohibited','direct')
 * @property {number} output_distance - The score the clicked article got from the KA model
 * @property {string} output_distance_str - The score the clicked article got from the KA model
 * @property {string} summarized_answer_based_on - The list of articles the generated answer was based on
 *
 * @property {number} summarized_answer_words_count - Number of words the generated answer was include
 *
 * @return {LogObject<clickOnAnswerParams>} LogObject<clickOnAnswerParams>
 */
export function clickOnAnswer(params) {
    return { evid: 521, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'clickTheSeeAllResultsLink' event (evid: 522)
 *
 * Event description: Fired when the user clicks the option to see all search results
 *
 * @param {clickTheSeeAllResultsLinkParams} params - The parameters for this event
 * @property {string} question - text entered by the user in the search bar
 * @property {string} item_id - item_id
 * @property {string} search_method - search_method
 * @property {string} search_session_Id - search_session_id
 * @property {string} source_name - indicates what type of article the user is at:
 * home, article, category, search_results
 * @property {string} search_version - name of search model used
 * @property {string} kb_lang - kb page language
 * @property {string} lang - wix language
 * @property {string} item_type - item_type
 * @property {string} source_type_trigger - the source name of the source in which the user clicks the 'show x more'. If the user clicks the general 'show all results' value will get 'general'.
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<clickTheSeeAllResultsLinkParams>} LogObject<clickTheSeeAllResultsLinkParams>
 */
export function clickTheSeeAllResultsLink(params) {
    return { evid: 522, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'pageView' event (evid: 531)
 *
 * Event description: Fire when the user enters a page in the Help Center
 *
 * @param {pageViewParams} params - The parameters for this event
 * @property {string} item_url - item_url
 * @property {string} question - question
 * @property {string} item_id - item_id
 * @property {string} item_version - item_version
 * @property {string} referral_name - referral_name
 * @property {Url} referral_url - referral_url
 * @property {string} tab - tab
 * @property {string} filter_by - filter_by
 * @property {string} presented_content - presented_content
 * @property {string} personalized_content - Include all the personalized content on that page
 * @property {string} item_name - item_name
 * @property {string} source_name - indicates what page the user is at: home, article, category, search_results
 * @property {string} sort_by - sort_by
 * @property {string} kb_lang - kb page language
 * @property {string} lang - wix language
 * @property {string} item_type - item_type
 * @property {string} ab_test_version - ab_test_version
 * @property {number} screen_width - The user's screen width
 * @property {number} screen_height - The user's screen height
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @property {boolean} page_load_at_top - false if the page was load somewhere in the middle of the page
 * @return {LogObject<pageViewParams>} LogObject<pageViewParams>
 */
export function pageView(params) {
    return { evid: 531, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'gotNoAnswer' event (evid: 537)
 *
 * Event description: Fire when no results return after the user search in the Help Center
 *
 * @param {gotNoAnswerParams} params - The parameters for this event
 * @property {string} question - The search term the user searched for
 * @property {string} source_url - source_url
 * @property {string} search_method - search method
 * @property {string} source_name - indicates what page the user is at: home, article, category, search_results
 * @property {string} search_version - name of search model used
 * @property {string} kb_lang - kb page language
 * @property {string} lang - lng
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @property {string} search_session_id - search_session_id
 * @return {LogObject<gotNoAnswerParams>} LogObject<gotNoAnswerParams>
 */
export function gotNoAnswer(params) {
    return { evid: 537, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'pageClick' event (evid: 8201)
 *
 * Event description: Fired when a user clicks a link/button in an article/home/category page
 *
 * @param {pageClickParams} params - The parameters for this event
 * @property {string} clicked_text - The text that was clicked on
 * @property {string} item_id - id of the page presented to the user (article/category)
 * @property {string} clicked_item_order - order of the item clicked
 * @property {string} clicked_item_type - type of item clicked
 * @property {string} clicked_item_id - id of the article/category/subcategory the user is directed to
 * @property {boolean} is_page_content_click - to indicate if the user interacted with the content on the page
 * @property {string} source_name - "indicates what ""type"" of page the user is at: home, article, category, search_results"
 * @property {string} kb_lang - kb page language
 * @property {number} category_order - Order of the parent category cell
 * @property {string} item_type - indicates the type of article presented to the user: article, feature_request, known_issue
 * @property {string} clicked_url - Destination url the link takes the user to
 * @property {string} search_session_Id - search_session_id
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<pageClickParams>} LogObject<pageClickParams>
 */
export function pageClick(params) {
    return { evid: 8201, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'pageEngagement' event (evid: 8202)
 *
 * Event description: Fired every 10 seconds and indicates if within that timeframe the user performed one of the following actions: scroll, scroll all the way to the bottom of the screen, moved mouse, clicked.
 *
 * @param {pageEngagementParams} params - The parameters for this event
 * @property {string} item_id - id of the page presented to the user (article/category)
 * @property {boolean} max_scroll_at_first_position - indicates if the entire article is visible to the user without having to scroll
 * @property {string} tab - name of the tab the use is on in the page
 * @property {string} filter_by - filter selected by the user
 * @property {string} activity_type - indicates the type of activity done by the user on the page
 * @property {string} source_name - "indicates what ""type"" of article the user is at: home, article, category, search_results"
 * @property {string} sort_by - sorting option selected by the user
 * @property {number} max_scroll - max % of the page the user reached while visiting the page
 * @property {string} kb_lang - kb page language
 * @property {string} item_type - indicates the type of article presented to the user: article, feature_request, known_issue
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<pageEngagementParams>} LogObject<pageEngagementParams>
 */
export function pageEngagement(params) {
    return { evid: 8202, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'pageError' event (evid: 8203)
 *
 * Event description: Fire when an error is shown after a user clicked a link in one of the Help Center pages
 *
 * @param {pageErrorParams} params - The parameters for this event
 * @property {string} referral_url - url of the page the user was on when clicking the link that got an error
 * @property {string} error_origin - url link that triggered the error
 * @property {string} source_name - "indicates what ""type"" of article the user is at: home, article, category, search_results"
 * @property {string} error_type_id - id of the error sent in case of a broken link
 * @property {string} kb_lang - kb page language
 * @property {string} error_type_description - description of the error in case of a broken link
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<pageErrorParams>} LogObject<pageErrorParams>
 */
export function pageError(params) {
    return { evid: 8203, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'sortAndFilter' event (evid: 8403)
 *
 * Event description: Fire when a user sorts/filters page view in the Help Center
 *
 * @param {sortAndFilterParams} params - The parameters for this event
 * @property {string} item_id - id of the page presented to the user (article/category)
 * @property {string} tab - name of the tab the use is on in the page
 * @property {number} items_left - Number of items remains on the page following the filter
 * @property {string} filter_by - filtering option selected by the user
 * @property {string} search_term_filter - The search term the user used to filter the view
 * @property {string} source_name - "indicates what ""type"" of article the user is at: home, article, category, search_results"
 * @property {string} sort_by - sorting option selected by the user
 * @property {string} kb_lang - kb page language
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<sortAndFilterParams>} LogObject<sortAndFilterParams>
 */
export function sortAndFilter(params) {
    return { evid: 8403, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'submitWasItHelpfulForm' event (evid: 8404)
 *
 * Event description: Fire when a user submits an article feedback form
 *
 * @param {submitWasItHelpfulFormParams} params - The parameters for this event
 * @property {string} clicked_text - the answer the user selected
 * @property {GUID} item_id - article id
 * @property {boolean} thumbs_up - the value clicked on before getting to the feedback form
 * @property {string} feedback_details - feedback form version
 * @property {number} clicked_item_index - id of the feedback option the user choose
 * @property {string} presented_content - presented_content
 * @property {string} clicked_item_order - the order of the answer the user selected
 * @property {string} source_name - name pf the page the user is on
 * @property {string} kb_lang - kb page language
 * @property {string} free_text - text entered by the user in the free text box
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<submitWasItHelpfulFormParams>} LogObject<submitWasItHelpfulFormParams>
 */
export function submitWasItHelpfulForm(params) {
    return { evid: 8404, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'helpCenterBannerClick' event (evid: 8405)
 *
 * Event description: Fire when a user clicks a banner in the Help Center
 *
 * @param {helpCenterBannerClickParams} params - The parameters for this event
 * @property {string} clicked_text - The text that was clicked on
 * @property {string} banner_text - banner text
 * @property {string} item_id - id of the page presented to the user (article/category)
 * @property {string} banner_type - to indicate the type of banner: status page, help center, etc.
 * @property {string} banner_name - banner name
 * @property {string} clicked_item_type - clicked_item_type
 * @property {string} source_name - indicates type of page the user is at: home, article, category, search_results
 * @property {boolean} is_resolved - param from the Status page endpoint
 * @property {string} kb_lang - kb page language
 * @property {string} item_type - indicates the type of article presented to the user: article, feature_request, known_issue
 * @property {string} banner_language - banner_language
 * @property {string} clicked_url - Destination url the link takes the user to
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<helpCenterBannerClickParams>} LogObject<helpCenterBannerClickParams>
 */
export function helpCenterBannerClick(params) {
    return { evid: 8405, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'helpCenterBannerView' event (evid: 8406)
 *
 * Event description: Fire when the user see a banner inside a page in the Help Center.
 *
 * @param {helpCenterBannerViewParams} params - The parameters for this event
 * @property {string} banner_text - banner text
 * @property {string} item_id - item_id
 * @property {string} banner_type - to indicate the type of banner: status page, help center, etc.
 * @property {string} banner_name - banner name
 * @property {boolean} has_cta - true if the banner includes CTA (clickable item/link)
 * @property {string} source_name - indicates type of page the user is at: home, article, category, search_results
 * @property {boolean} is_resolved - param from the Status page endpoint i
 * @property {string} kb_lang - kb page language
 * @property {string} item_type - indicates the type of article presented to the user: article, feature_request, known_issue
 * @property {string} banner_language - lang of the banner
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<helpCenterBannerViewParams>} LogObject<helpCenterBannerViewParams>
 */
export function helpCenterBannerView(params) {
    return { evid: 8406, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'wasItHelpfulView' event (evid: 8407)
 *
 * Event description: fire when a certain item is visible on a Help Center page
 *
 * @param {wasItHelpfulViewParams} params - The parameters for this event
 * @property {GUID} item_id - article id
 * @property {string} feedback_details - feedback form version
 * @property {string} item_name - item viewed
 * @property {string} source_name - name of the page the user is on (e.g. article, home etc.)
 * @property {string} kb_lang - kb page language
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<wasItHelpfulViewParams>} LogObject<wasItHelpfulViewParams>
 */
export function wasItHelpfulView(params) {
    return { evid: 8407, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'roadmapWishlistFormClick' event (evid: 8411)
 *
 * Event description: fires when a user clicks one of the buttons in the roadmap wishlist form
 *
 * @param {roadmapWishlistFormClickParams} params - The parameters for this event
 * @property {string} form_step - {'main','alternative_suggester'}
 * @property {string} chosen_alternative_resolution - Only if the user was presented with alternative {'planned','collecting votes'}
 * @property {string} clicked_item_type - the button clicked by the user (close/send/cancel, etc.)
 * @property {string} source_name - name pf the page the user is on
 * @property {string} origin - where the user got to the form from
 * @property {string} feature_description - The text the user added for the  'describe the feature' parameter when sent the form
 * @property {string} origin_url - where the user got to the form from (url)
 * @property {string} feature_impact - The text the user added for the  'how the feature will make an impact' parameter when sent the form
 * @property {string} kb_lang - kb page language
 * @property {string} product - The value the user chose for the 'product' parameter when sent the form
 * @property {GUID} chosen_alternative_id - The chosen alternative article_id - only if the user presented with alternative and clicked 'follow' or 'vote'
 * @property {string} output_distance_str - The score the alternative feature requests that was suggested got from the model
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @return {LogObject<roadmapWishlistFormClickParams>} LogObject<roadmapWishlistFormClickParams>
 */
export function roadmapWishlistFormClick(params) {
    return { evid: 8411, src: 4, endpoint: 'new-help-center', params: params };
}
/**
 * A function to create a log object for the 'helpCenterModals' event (evid: 8414)
 *
 * Event description: Fire when a user interacts with a modal in the Help Center
 *
 * @param {helpCenterModalsParams} params - The parameters for this event
 * @property {string} item_id - ID of the page the modal was presented on (article/category)
 * @property {string} screen_name - The specific screen inside the modal the user interacted with
 * @property {string} clicked_button_name - The button name inside the modal the user clicked on
 * @property {string} modal_name - The name of the modal the user interacted with
 * @property {string} source_name - indicates type of page the user is at when modal is open: home, article, category, search_results
 * @property {string} kb_lang - kb page language
 * @property {string} action_type - The action type the user performed inside the modal
 * @property {GUID} self_service_session_id - The CC cross self service platforms session the user was part of
 * @property {GUID} modal_session_id - random unique guid which will follow with all actions and screens of the modal
 * @property {string} origin - where the user got to the modal from
 * @property {string} params - more information about the modal parameters and content
 * @property {GUID} clicked_item_id - the item id that was chosen inside the modal
 * @property {string} alternatives_info - information about the alternatives items that were presented inside the modal
 * @return {LogObject<helpCenterModalsParams>} LogObject<helpCenterModalsParams>
 */
export function helpCenterModals(params) {
    return { evid: 8414, src: 4, endpoint: 'new-help-center', params: params };
}
//# sourceMappingURL=index.js.map